// SPDX-License-Identifier: AGPL-3.0-or-later
import type {Icon, IconWeight} from '@phosphor-icons/react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
export interface SettingsModalLayoutProps {
	fullscreen: boolean;
}
export interface SidebarCategoryContextValue {
	setTitleId: (id: string | null) => void;
}
export interface SidebarTablistContextValue {
	hasSelectedTabInView: boolean;
}
export interface SettingsModalSidebarItemProps {
	label: React.ReactNode;
	icon: Icon;
	iconWeight?: IconWeight;
	selected?: boolean;
	danger?: boolean;
	onClick?: () => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	onRequestContentFocus?: () => void;
	id?: string;
	controlsId?: string;
}
export interface SettingsModalSidebarItemLogicState {
	tabIndex: number;
	handleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	buttonRef: React.RefObject<HTMLButtonElement | null>;
}
export function useWidescreenMode(): boolean {
	const [isWidescreenMode, setIsWidescreenMode] = useState(() => window.matchMedia('(min-width: 2000px)').matches);
	useEffect(() => {
		const mediaQuery = window.matchMedia('(min-width: 2000px)');
		const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
			setIsWidescreenMode(e.matches);
		};
		handleChange(mediaQuery);
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);
	return isWidescreenMode;
}
const SIDEBAR_LIST_SELECTOR = '[data-settings-sidebar-list]';
const SIDEBAR_ITEM_SELECTOR = '[data-settings-sidebar-item="true"]';
const SIDEBAR_TAB_SELECTOR = '[data-settings-tab="true"]';
const isSidebarTab = (element: HTMLElement): boolean => element.matches(SIDEBAR_TAB_SELECTOR);
const getSidebarItemsWithinList = (
	current: HTMLElement,
	selector = SIDEBAR_ITEM_SELECTOR,
): Array<HTMLButtonElement> => {
	const list = current.closest<HTMLElement>(SIDEBAR_LIST_SELECTOR);
	if (!list) return [];
	return Array.from(list.querySelectorAll<HTMLButtonElement>(selector));
};
const getSidebarNavigationItems = (current: HTMLButtonElement): Array<HTMLButtonElement> => {
	return getSidebarItemsWithinList(current, isSidebarTab(current) ? SIDEBAR_TAB_SELECTOR : SIDEBAR_ITEM_SELECTOR);
};
const activateSidebarTabIfNeeded = (current: HTMLButtonElement, target: HTMLButtonElement) => {
	if (target !== current && isSidebarTab(current) && isSidebarTab(target)) {
		target.click();
	}
};
const focusRelativeSidebarItem = (current: HTMLButtonElement, offset: number) => {
	const items = getSidebarNavigationItems(current);
	if (!items.length) return null;
	const currentIndex = items.indexOf(current);
	if (currentIndex === -1) return null;
	const nextIndex = (currentIndex + offset + items.length) % items.length;
	const target = items[nextIndex] ?? null;
	target?.focus();
	return target;
};
const focusEdgeSidebarItem = (current: HTMLButtonElement, edge: 'first' | 'last') => {
	const items = getSidebarNavigationItems(current);
	if (!items.length) return null;
	const target = edge === 'first' ? items[0] : items[items.length - 1];
	target?.focus();
	return target ?? null;
};
export function useSettingsModalSidebarItemLogic({
	selected,
	onClick,
	onKeyDown,
	onRequestContentFocus,
	hasSelectedTabInView,
}: Pick<SettingsModalSidebarItemProps, 'selected' | 'onClick' | 'onKeyDown' | 'onRequestContentFocus'> & {
	hasSelectedTabInView: boolean;
}): SettingsModalSidebarItemLogicState {
	const ref = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		if (selected) {
			ref.current?.scrollIntoView({block: 'nearest'});
		}
	}, [selected]);
	const tabIndex = useMemo(() => {
		if (!hasSelectedTabInView) return 0;
		return selected ? 0 : -1;
	}, [hasSelectedTabInView, selected]);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>) => {
			if (event.defaultPrevented) {
				onKeyDown?.(event);
				return;
			}
			switch (event.key) {
				case 'ArrowDown': {
					const target = focusRelativeSidebarItem(event.currentTarget, 1);
					if (target) {
						activateSidebarTabIfNeeded(event.currentTarget, target);
					}
					event.preventDefault();
					break;
				}
				case 'ArrowUp': {
					const target = focusRelativeSidebarItem(event.currentTarget, -1);
					if (target) {
						activateSidebarTabIfNeeded(event.currentTarget, target);
					}
					event.preventDefault();
					break;
				}
				case 'Home': {
					const target = focusEdgeSidebarItem(event.currentTarget, 'first');
					if (target) {
						activateSidebarTabIfNeeded(event.currentTarget, target);
					}
					event.preventDefault();
					break;
				}
				case 'End': {
					const target = focusEdgeSidebarItem(event.currentTarget, 'last');
					if (target) {
						activateSidebarTabIfNeeded(event.currentTarget, target);
					}
					event.preventDefault();
					break;
				}
				case 'Enter':
				case ' ': {
					onClick?.();
					onRequestContentFocus?.();
					event.preventDefault();
					break;
				}
				default:
					break;
			}
			onKeyDown?.(event);
		},
		[onClick, onKeyDown, onRequestContentFocus],
	);
	return {
		tabIndex,
		handleKeyDown,
		buttonRef: ref,
	};
}
export function focusSelectedSidebarTab(navElement: HTMLElement | null): boolean {
	if (!navElement) return false;
	const list = navElement.querySelector<HTMLElement>(SIDEBAR_LIST_SELECTOR);
	if (!list) return false;
	const selected = list.querySelector<HTMLButtonElement>(`${SIDEBAR_TAB_SELECTOR}[aria-current="page"]`);
	const fallback = list.querySelector<HTMLButtonElement>(SIDEBAR_TAB_SELECTOR);
	const target = selected ?? fallback;
	if (!target) return false;
	target.focus();
	return true;
}
export function handleSidebarNavKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
	if (event.defaultPrevented) return;
	const {key} = event;
	if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;
	const active = document.activeElement as HTMLElement | null;
	if (active?.matches(SIDEBAR_ITEM_SELECTOR)) return;
	if (focusSelectedSidebarTab(event.currentTarget)) {
		event.preventDefault();
	}
}
export function useTrafficLightsVisibility(fullscreen: boolean, isWidescreenMode: boolean): boolean {
	return useMemo(() => {
		return fullscreen && !isWidescreenMode;
	}, [fullscreen, isWidescreenMode]);
}
